// src/enums.ts
var Suit = /* @__PURE__ */ ((Suit4) => {
  Suit4["Spades"] = "spades";
  Suit4["Hearts"] = "hearts";
  Suit4["Diamonds"] = "diamonds";
  Suit4["Clubs"] = "clubs";
  return Suit4;
})(Suit || {});
var Rank = /* @__PURE__ */ ((Rank2) => {
  Rank2[Rank2["Two"] = 2] = "Two";
  Rank2[Rank2["Three"] = 3] = "Three";
  Rank2[Rank2["Four"] = 4] = "Four";
  Rank2[Rank2["Five"] = 5] = "Five";
  Rank2[Rank2["Six"] = 6] = "Six";
  Rank2[Rank2["Seven"] = 7] = "Seven";
  Rank2[Rank2["Eight"] = 8] = "Eight";
  Rank2[Rank2["Nine"] = 9] = "Nine";
  Rank2[Rank2["Ten"] = 10] = "Ten";
  Rank2[Rank2["Jack"] = 11] = "Jack";
  Rank2[Rank2["Queen"] = 12] = "Queen";
  Rank2[Rank2["King"] = 13] = "King";
  Rank2[Rank2["Ace"] = 14] = "Ace";
  return Rank2;
})(Rank || {});
var GamePhase = /* @__PURE__ */ ((GamePhase2) => {
  GamePhase2["Waiting"] = "waiting";
  GamePhase2["Calling"] = "calling";
  GamePhase2["Playing"] = "playing";
  GamePhase2["RoundEnd"] = "round_end";
  GamePhase2["GameOver"] = "game_over";
  return GamePhase2;
})(GamePhase || {});
var PlayerType = /* @__PURE__ */ ((PlayerType2) => {
  PlayerType2["Human"] = "human";
  PlayerType2["AI"] = "ai";
  return PlayerType2;
})(PlayerType || {});
var AIDifficulty = /* @__PURE__ */ ((AIDifficulty2) => {
  AIDifficulty2["Easy"] = "easy";
  AIDifficulty2["Medium"] = "medium";
  AIDifficulty2["Hard"] = "hard";
  return AIDifficulty2;
})(AIDifficulty || {});
var TRUMP_SUIT = "spades" /* Spades */;
var MIN_CALL = 2;
var MAX_CALL = 13;
var WINNING_SCORE = 50;
var BONUS_CALL_THRESHOLD = 6;
var CARDS_PER_PLAYER = 13;
var MAX_PLAYERS = 4;
var MIN_PLAYERS = 2;

// src/Card.ts
function createCard(suit, rank) {
  return { suit, rank };
}
function isTrump(card) {
  return card.suit === TRUMP_SUIT;
}
function isHigherRank(a, b) {
  return a.rank > b.rank;
}
function compareCards(a, b, leadSuit) {
  const aIsTrump = isTrump(a);
  const bIsTrump = isTrump(b);
  if (aIsTrump && !bIsTrump) return 1;
  if (!aIsTrump && bIsTrump) return -1;
  if (aIsTrump && bIsTrump) return a.rank - b.rank;
  const aFollows = a.suit === leadSuit;
  const bFollows = b.suit === leadSuit;
  if (aFollows && !bFollows) return 1;
  if (!aFollows && bFollows) return -1;
  if (aFollows && bFollows) return a.rank - b.rank;
  return 0;
}
function cardEquals(a, b) {
  return a.suit === b.suit && a.rank === b.rank;
}
function cardToString(card) {
  const rankNames = {
    [2 /* Two */]: "2",
    [3 /* Three */]: "3",
    [4 /* Four */]: "4",
    [5 /* Five */]: "5",
    [6 /* Six */]: "6",
    [7 /* Seven */]: "7",
    [8 /* Eight */]: "8",
    [9 /* Nine */]: "9",
    [10 /* Ten */]: "10",
    [11 /* Jack */]: "J",
    [12 /* Queen */]: "Q",
    [13 /* King */]: "K",
    [14 /* Ace */]: "A"
  };
  const suitSymbols = {
    ["spades" /* Spades */]: "\u2660",
    ["hearts" /* Hearts */]: "\u2665",
    ["diamonds" /* Diamonds */]: "\u2666",
    ["clubs" /* Clubs */]: "\u2663"
  };
  return `${rankNames[card.rank]}${suitSymbols[card.suit]}`;
}

// src/cardUtils.ts
var SUIT_ORDER = ["spades" /* Spades */, "hearts" /* Hearts */, "diamonds" /* Diamonds */, "clubs" /* Clubs */];
function sortHand(hand) {
  return [...hand].sort((a, b) => {
    const suitDiff = SUIT_ORDER.indexOf(a.suit) - SUIT_ORDER.indexOf(b.suit);
    if (suitDiff !== 0) return suitDiff;
    return b.rank - a.rank;
  });
}
function groupBySuit(hand) {
  const groups = {
    ["spades" /* Spades */]: [],
    ["hearts" /* Hearts */]: [],
    ["diamonds" /* Diamonds */]: [],
    ["clubs" /* Clubs */]: []
  };
  for (const card of hand) {
    groups[card.suit].push(card);
  }
  for (const suit of SUIT_ORDER) {
    groups[suit].sort((a, b) => b.rank - a.rank);
  }
  return groups;
}
function getRankName(rank) {
  const names = {
    [2 /* Two */]: "Two",
    [3 /* Three */]: "Three",
    [4 /* Four */]: "Four",
    [5 /* Five */]: "Five",
    [6 /* Six */]: "Six",
    [7 /* Seven */]: "Seven",
    [8 /* Eight */]: "Eight",
    [9 /* Nine */]: "Nine",
    [10 /* Ten */]: "Ten",
    [11 /* Jack */]: "Jack",
    [12 /* Queen */]: "Queen",
    [13 /* King */]: "King",
    [14 /* Ace */]: "Ace"
  };
  return names[rank] ?? "Unknown";
}
function getCardsOfSuit(hand, suit) {
  return hand.filter((c) => c.suit === suit);
}
function hasCardOfSuit(hand, suit) {
  return hand.some((c) => c.suit === suit);
}

// src/Deck.ts
var ALL_SUITS = ["spades" /* Spades */, "hearts" /* Hearts */, "diamonds" /* Diamonds */, "clubs" /* Clubs */];
var ALL_RANKS = [
  2 /* Two */,
  3 /* Three */,
  4 /* Four */,
  5 /* Five */,
  6 /* Six */,
  7 /* Seven */,
  8 /* Eight */,
  9 /* Nine */,
  10 /* Ten */,
  11 /* Jack */,
  12 /* Queen */,
  13 /* King */,
  14 /* Ace */
];
function createDeck() {
  const deck = [];
  for (const suit of ALL_SUITS) {
    for (const rank of ALL_RANKS) {
      deck.push({ suit, rank });
    }
  }
  return deck;
}
function shuffle(deck) {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function deal(playerCount) {
  const deck = shuffle(createDeck());
  const hands = Array.from({ length: playerCount }, () => []);
  const cardsPerPlayer = Math.min(CARDS_PER_PLAYER, Math.floor(deck.length / playerCount));
  for (let i = 0; i < cardsPerPlayer * playerCount; i++) {
    hands[i % playerCount].push(deck[i]);
  }
  return hands;
}

// src/Trick.ts
function createTrick() {
  return {
    cards: [],
    leadSuit: null,
    winnerId: null,
    isComplete: false
  };
}
function getLeadSuit(trick) {
  return trick.leadSuit;
}
function playCardToTrick(trick, playerId, card, playerCount) {
  const trickCard = { playerId, card };
  const cards = [...trick.cards, trickCard];
  const leadSuit = trick.leadSuit ?? card.suit;
  const isComplete = cards.length === playerCount;
  const winnerId = isComplete ? determineTrickWinner(cards, leadSuit) : null;
  return {
    cards,
    leadSuit,
    winnerId,
    isComplete
  };
}
function determineTrickWinner(cards, leadSuit) {
  if (cards.length === 0) {
    throw new Error("Cannot determine winner of empty trick");
  }
  let winningCard = cards[0];
  for (let i = 1; i < cards.length; i++) {
    const current = cards[i];
    const comparison = compareCards(
      current.card,
      winningCard.card,
      leadSuit
    );
    if (comparison > 0) {
      winningCard = current;
    }
  }
  return winningCard.playerId;
}

// src/Scoring.ts
function calculateRoundScore(call, tricksWon) {
  if (tricksWon < call) {
    return -call;
  }
  const overTricks = tricksWon - call;
  if (overTricks >= 2) {
    return -call;
  }
  let score = call;
  if (call >= BONUS_CALL_THRESHOLD && overTricks === 0) {
    score = call * 2;
  }
  if (overTricks === 1) {
    score = call;
  }
  return score;
}
function calculateRoundResults(players, roundNumber) {
  const playerResults = players.map((p) => {
    const roundScore = calculateRoundScore(p.call, p.tricksWon);
    const totalScore = p.currentScore + roundScore;
    return {
      playerId: p.playerId,
      call: p.call,
      tricksWon: p.tricksWon,
      roundScore,
      totalScore,
      overCall: Math.max(0, p.tricksWon - p.call)
    };
  });
  return {
    roundNumber,
    playerResults
  };
}
function isGameOver(scores) {
  return scores.some((s) => s >= WINNING_SCORE);
}
function getWinner(players) {
  const eligible = players.filter((p) => p.score >= WINNING_SCORE);
  if (eligible.length === 0) return null;
  if (eligible.length === 1) return eligible[0].id;
  eligible.sort((a, b) => b.score - a.score);
  if (eligible[0].score > eligible[1].score) {
    return eligible[0].id;
  }
  return null;
}

// src/MoveValidator.ts
function isValidMove(card, player, trick, currentPlayerIndex, expectedPlayerIndex, phase) {
  if (phase !== "playing" /* Playing */) {
    return { valid: false, reason: "Not in playing phase" };
  }
  if (currentPlayerIndex !== expectedPlayerIndex) {
    return { valid: false, reason: "Not your turn" };
  }
  const hasCard = player.hand.some((c) => cardEquals(c, card));
  if (!hasCard) {
    return { valid: false, reason: "Card not in hand" };
  }
  if (trick.leadSuit !== null) {
    const mustFollowSuit = hasCardOfSuit(player.hand, trick.leadSuit);
    if (mustFollowSuit && card.suit !== trick.leadSuit) {
      return { valid: false, reason: `Must follow suit: ${trick.leadSuit}` };
    }
  }
  return { valid: true };
}

// src/CallValidator.ts
function isValidCall(call, phase, currentPlayerIndex, expectedPlayerIndex) {
  if (phase !== "calling" /* Calling */) {
    return { valid: false, reason: "Not in calling phase" };
  }
  if (currentPlayerIndex !== expectedPlayerIndex) {
    return { valid: false, reason: "Not your turn to call" };
  }
  if (!Number.isInteger(call)) {
    return { valid: false, reason: "Call must be an integer" };
  }
  if (call < MIN_CALL || call > MAX_CALL) {
    return { valid: false, reason: `Call must be between ${MIN_CALL} and ${MAX_CALL}` };
  }
  return { valid: true };
}

// src/Player.ts
function createPlayer(id, name, type = "human" /* Human */, aiDifficulty) {
  return {
    id,
    name,
    type,
    aiDifficulty,
    hand: [],
    call: null,
    tricksWon: 0,
    score: 0,
    isConnected: type === "human" /* Human */
  };
}
function dealToPlayer(player, cards) {
  return {
    ...player,
    hand: cards,
    call: null,
    tricksWon: 0
  };
}
function setCall(player, call) {
  return {
    ...player,
    call
  };
}
function removeCardFromHand(player, card) {
  return {
    ...player,
    hand: player.hand.filter((c) => !(c.suit === card.suit && c.rank === card.rank))
  };
}
function incrementTricksWon(player) {
  return {
    ...player,
    tricksWon: player.tricksWon + 1
  };
}
function updateScore(player, roundScore) {
  return {
    ...player,
    score: player.score + roundScore
  };
}

// src/Round.ts
function createRound(players, roundNumber, dealerIndex) {
  const hands = deal(players.length);
  const dealtPlayers = players.map(
    (p, i) => dealToPlayer(p, sortHand(hands[i]))
  );
  const firstPlayerIndex = (dealerIndex + 1) % players.length;
  const isOpenRound = roundNumber === 1;
  return {
    roundNumber,
    dealerIndex,
    currentPlayerIndex: firstPlayerIndex,
    phase: isOpenRound ? "playing" /* Playing */ : "calling" /* Calling */,
    players: dealtPlayers,
    currentTrick: createTrick(),
    tricksPlayed: 0,
    calls: {}
  };
}
function makeCall(round, playerId, call) {
  const playerIndex = round.players.findIndex((p) => p.id === playerId);
  if (playerIndex === -1) {
    throw new Error(`Player ${playerId} not found`);
  }
  const validation = isValidCall(call, round.phase, playerIndex, round.currentPlayerIndex);
  if (!validation.valid) {
    throw new Error(validation.reason);
  }
  const updatedPlayers = round.players.map(
    (p, i) => i === playerIndex ? setCall(p, call) : p
  );
  const calls = { ...round.calls, [playerId]: call };
  const allCalled = Object.keys(calls).length === round.players.length;
  const nextPlayerIndex = (round.currentPlayerIndex + 1) % round.players.length;
  return {
    ...round,
    players: updatedPlayers,
    calls,
    currentPlayerIndex: allCalled ? (round.dealerIndex + 1) % round.players.length : nextPlayerIndex,
    phase: allCalled ? "playing" /* Playing */ : "calling" /* Calling */,
    currentTrick: allCalled ? createTrick() : round.currentTrick
  };
}
function playCard(round, playerId, card) {
  const playerIndex = round.players.findIndex((p) => p.id === playerId);
  if (playerIndex === -1) {
    throw new Error(`Player ${playerId} not found`);
  }
  const validation = isValidMove(
    card,
    round.players[playerIndex],
    round.currentTrick,
    playerIndex,
    round.currentPlayerIndex,
    round.phase
  );
  if (!validation.valid) {
    throw new Error(validation.reason);
  }
  const updatedPlayers = round.players.map(
    (p, i) => i === playerIndex ? removeCardFromHand(p, card) : p
  );
  const updatedTrick = playCardToTrick(
    round.currentTrick,
    playerId,
    card,
    round.players.length
  );
  if (updatedTrick.isComplete && updatedTrick.winnerId) {
    const winnerIndex = round.players.findIndex((p) => p.id === updatedTrick.winnerId);
    const playersWithTrick = updatedPlayers.map(
      (p, i) => i === winnerIndex ? incrementTricksWon(p) : p
    );
    const tricksPlayed = round.tricksPlayed + 1;
    const roundComplete = tricksPlayed === CARDS_PER_PLAYER;
    return {
      ...round,
      players: playersWithTrick,
      currentTrick: roundComplete ? updatedTrick : createTrick(),
      tricksPlayed,
      currentPlayerIndex: roundComplete ? round.currentPlayerIndex : winnerIndex,
      phase: roundComplete ? "round_end" /* RoundEnd */ : "playing" /* Playing */
    };
  }
  const nextPlayerIndex = (round.currentPlayerIndex + 1) % round.players.length;
  return {
    ...round,
    players: updatedPlayers,
    currentTrick: updatedTrick,
    currentPlayerIndex: nextPlayerIndex
  };
}

// src/actions.ts
var ActionType = /* @__PURE__ */ ((ActionType2) => {
  ActionType2["JoinGame"] = "join_game";
  ActionType2["LeaveGame"] = "leave_game";
  ActionType2["StartGame"] = "start_game";
  ActionType2["MakeCall"] = "make_call";
  ActionType2["PlayCard"] = "play_card";
  return ActionType2;
})(ActionType || {});

// src/Game.ts
var DEFAULT_SETTINGS = {
  maxPlayers: MAX_PLAYERS,
  turnTimeoutMs: 3e4,
  winningScore: WINNING_SCORE
};
function createGame(id, settings = {}) {
  return {
    id,
    phase: "waiting" /* Waiting */,
    players: [],
    currentRound: null,
    roundResults: [],
    roundNumber: 0,
    winnerId: null,
    settings: { ...DEFAULT_SETTINGS, ...settings },
    createdAt: Date.now()
  };
}
function addPlayer(game, playerId, playerName) {
  if (game.phase !== "waiting" /* Waiting */) {
    throw new Error("Cannot join game that is not in waiting phase");
  }
  if (game.players.length >= game.settings.maxPlayers) {
    throw new Error("Game is full");
  }
  if (game.players.some((p) => p.id === playerId)) {
    throw new Error("Player already in game");
  }
  const player = createPlayer(playerId, playerName);
  return {
    ...game,
    players: [...game.players, player]
  };
}
function removePlayer(game, playerId) {
  if (game.phase !== "waiting" /* Waiting */) {
    throw new Error("Cannot leave game that is not in waiting phase");
  }
  if (!game.players.some((p) => p.id === playerId)) {
    throw new Error("Player not found in game");
  }
  return {
    ...game,
    players: game.players.filter((p) => p.id !== playerId)
  };
}
function startGame(game) {
  if (game.phase !== "waiting" /* Waiting */) {
    throw new Error("Game is not in waiting phase");
  }
  if (game.players.length < MIN_PLAYERS) {
    throw new Error(`Need at least ${MIN_PLAYERS} players to start`);
  }
  const dealerIndex = 0;
  const round = createRound(game.players, 1, dealerIndex);
  return {
    ...game,
    phase: round.phase,
    currentRound: round,
    roundNumber: 1
  };
}
function processCall(game, playerId, call) {
  if (game.phase !== "calling" /* Calling */ || !game.currentRound) {
    throw new Error("Not in calling phase");
  }
  const updatedRound = makeCall(game.currentRound, playerId, call);
  return {
    ...game,
    currentRound: updatedRound,
    phase: updatedRound.phase
  };
}
function processPlayCard(game, playerId, card) {
  if (game.phase !== "playing" /* Playing */ || !game.currentRound) {
    throw new Error("Not in playing phase");
  }
  const updatedRound = playCard(game.currentRound, playerId, card);
  if (updatedRound.phase === "round_end" /* RoundEnd */) {
    return resolveRound({
      ...game,
      currentRound: updatedRound
    });
  }
  return {
    ...game,
    currentRound: updatedRound,
    phase: updatedRound.phase
  };
}
function resolveRound(game) {
  if (!game.currentRound) {
    throw new Error("No current round to resolve");
  }
  const isOpenRound = game.roundNumber === 1;
  const scoringInputs = game.currentRound.players.map((p) => ({
    playerId: p.id,
    call: isOpenRound ? p.tricksWon : p.call,
    tricksWon: p.tricksWon,
    currentScore: p.score
  }));
  const roundResult = calculateRoundResults(scoringInputs, game.roundNumber);
  const updatedPlayers = game.players.map((player) => {
    const result = roundResult.playerResults.find((r) => r.playerId === player.id);
    if (!result) return player;
    return updateScore(player, result.roundScore);
  });
  const scores = updatedPlayers.map((p) => p.score);
  const gameOver = isGameOver(scores);
  if (gameOver) {
    const winnerId = getWinner(updatedPlayers);
    if (winnerId) {
      return {
        ...game,
        players: updatedPlayers,
        currentRound: null,
        roundResults: [...game.roundResults, roundResult],
        phase: "game_over" /* GameOver */,
        winnerId
      };
    }
  }
  const nextRoundNumber = game.roundNumber + 1;
  const nextDealerIndex = (game.currentRound.dealerIndex + 1) % updatedPlayers.length;
  const nextRound = createRound(updatedPlayers, nextRoundNumber, nextDealerIndex);
  return {
    ...game,
    players: updatedPlayers,
    currentRound: nextRound,
    roundResults: [...game.roundResults, roundResult],
    roundNumber: nextRoundNumber,
    phase: nextRound.phase
  };
}
function gameReducer(game, action) {
  switch (action.type) {
    case "join_game" /* JoinGame */:
      return addPlayer(game, action.playerId, action.playerName);
    case "leave_game" /* LeaveGame */:
      return removePlayer(game, action.playerId);
    case "start_game" /* StartGame */:
      return startGame(game);
    case "make_call" /* MakeCall */:
      return processCall(game, action.playerId, action.call);
    case "play_card" /* PlayCard */:
      return processPlayCard(game, action.playerId, action.card);
    default:
      return game;
  }
}

// src/events.ts
var GameEventType = /* @__PURE__ */ ((GameEventType2) => {
  GameEventType2["GameCreated"] = "game_created";
  GameEventType2["PlayerJoined"] = "player_joined";
  GameEventType2["PlayerLeft"] = "player_left";
  GameEventType2["GameStarted"] = "game_started";
  GameEventType2["RoundStarted"] = "round_started";
  GameEventType2["CallMade"] = "call_made";
  GameEventType2["CardPlayed"] = "card_played";
  GameEventType2["TrickCompleted"] = "trick_completed";
  GameEventType2["RoundCompleted"] = "round_completed";
  GameEventType2["GameCompleted"] = "game_completed";
  return GameEventType2;
})(GameEventType || {});

// src/ai/CallEstimator.ts
function countGuaranteedTricks(hand) {
  let count = 0;
  for (const card of hand) {
    if (isTrump(card) && card.rank === 14 /* Ace */) {
      count++;
    } else if (isTrump(card) && card.rank === 13 /* King */) {
      count++;
    } else if (!isTrump(card) && card.rank === 14 /* Ace */) {
      count++;
    }
  }
  return count;
}
function evaluateHandStrength(hand) {
  const groups = groupBySuit(hand);
  const trumpCards = hand.filter((c) => isTrump(c));
  const trumpCount = trumpCards.length;
  let voidCount = 0;
  for (const [suit, cards] of Object.entries(groups)) {
    if (suit !== TRUMP_SUIT && cards.length === 0) {
      voidCount++;
    }
  }
  const guaranteedTricks = countGuaranteedTricks(hand);
  let estimatedTricks = guaranteedTricks;
  for (const card of hand) {
    if (!isTrump(card) && card.rank === 13 /* King */) {
      estimatedTricks += 0.5;
    }
  }
  for (const card of hand) {
    if (!isTrump(card) && card.rank === 12 /* Queen */) {
      estimatedTricks += 0.25;
    }
  }
  if (trumpCount > 2) {
    const ruffPotential = Math.min(trumpCount - 2, voidCount);
    estimatedTricks += ruffPotential * 0.7;
  }
  for (const card of trumpCards) {
    if (card.rank === 12 /* Queen */) {
      estimatedTricks += 0.4;
    } else if (card.rank === 11 /* Jack */) {
      estimatedTricks += 0.2;
    }
  }
  const strengthScore = Math.min(100, Math.round(estimatedTricks / 13 * 100));
  return {
    estimatedTricks: Math.round(estimatedTricks),
    guaranteedTricks,
    trumpCount,
    voidCount,
    strengthScore
  };
}
function suggestCall(hand) {
  const strength = evaluateHandStrength(hand);
  const suggested = Math.max(MIN_CALL, Math.min(MAX_CALL, strength.estimatedTricks));
  return suggested;
}

// src/ai/EasyAI.ts
var EasyAI = class {
  makeCall(_player, _round) {
    return MIN_CALL + Math.floor(Math.random() * 3);
  }
  selectCard(player, round) {
    const validCards = this.getValidCards(player, round);
    if (validCards.length === 0) {
      throw new Error("No valid cards to play");
    }
    const index = Math.floor(Math.random() * validCards.length);
    return validCards[index];
  }
  getValidCards(player, round) {
    const leadSuit = round.currentTrick.leadSuit;
    if (leadSuit === null) {
      return [...player.hand];
    }
    if (hasCardOfSuit(player.hand, leadSuit)) {
      return getCardsOfSuit(player.hand, leadSuit);
    }
    return [...player.hand];
  }
};
function createEasyAI() {
  return new EasyAI();
}

// src/ai/MediumAI.ts
var MediumAI = class {
  makeCall(player, _round) {
    return suggestCall(player.hand);
  }
  selectCard(player, round) {
    const leadSuit = round.currentTrick.leadSuit;
    const hand = player.hand;
    if (hand.length === 0) {
      throw new Error("No cards to play");
    }
    if (leadSuit === null) {
      return this.selectLeadCard(hand);
    }
    if (hasCardOfSuit(hand, leadSuit)) {
      return this.selectFollowCard(hand, round, leadSuit);
    }
    return this.selectVoidCard(hand, round);
  }
  /**
   * When leading a trick, prefer strong non-trump cards to draw out
   * opponents' high cards. Lead aces first, then kings.
   * Save trumps for later unless they're the best option.
   */
  selectLeadCard(hand) {
    const nonTrumpAce = hand.find((c) => !isTrump(c) && c.rank === 14 /* Ace */);
    if (nonTrumpAce) return nonTrumpAce;
    const nonTrumpKing = hand.find((c) => !isTrump(c) && c.rank === 13 /* King */);
    if (nonTrumpKing) return nonTrumpKing;
    const nonTrumps = hand.filter((c) => !isTrump(c));
    if (nonTrumps.length > 0) {
      nonTrumps.sort((a, b) => b.rank - a.rank);
      return nonTrumps[0];
    }
    const sorted = [...hand].sort((a, b) => b.rank - a.rank);
    return sorted[0];
  }
  /**
   * When following suit:
   * - If we can win the trick, play the lowest winning card
   * - If we can't win, play the lowest card to minimize loss
   */
  selectFollowCard(hand, round, leadSuit) {
    const suitCards = getCardsOfSuit(hand, leadSuit);
    suitCards.sort((a, b) => a.rank - b.rank);
    const currentWinner = this.getCurrentWinningCard(round);
    if (currentWinner) {
      const winners = suitCards.filter(
        (c) => compareCards(c, currentWinner, leadSuit) > 0
      );
      if (winners.length > 0) {
        return winners[0];
      }
    }
    return suitCards[0];
  }
  /**
   * When void in lead suit:
   * - If the trick is worth winning (high cards played), trump it
   * - Otherwise, discard the lowest card from the weakest suit
   */
  selectVoidCard(hand, round) {
    const trumpCards = hand.filter((c) => isTrump(c));
    const nonTrumpCards = hand.filter((c) => !isTrump(c));
    if (trumpCards.length > 0) {
      const trickHasHighCards = round.currentTrick.cards.some(
        (tc) => tc.card.rank >= 12 /* Queen */
      );
      if (trickHasHighCards || round.tricksPlayed < 6) {
        const existingTrump = round.currentTrick.cards.find(
          (tc) => isTrump(tc.card)
        );
        if (existingTrump) {
          const overTrumps = trumpCards.filter((c) => c.rank > existingTrump.card.rank).sort((a, b) => a.rank - b.rank);
          if (overTrumps.length > 0) {
            return overTrumps[0];
          }
        } else {
          trumpCards.sort((a, b) => a.rank - b.rank);
          return trumpCards[0];
        }
      }
    }
    if (nonTrumpCards.length > 0) {
      nonTrumpCards.sort((a, b) => a.rank - b.rank);
      return nonTrumpCards[0];
    }
    trumpCards.sort((a, b) => a.rank - b.rank);
    return trumpCards[0];
  }
  /**
   * Get the card that's currently winning the trick.
   */
  getCurrentWinningCard(round) {
    const trick = round.currentTrick;
    if (trick.cards.length === 0) return null;
    const leadSuit = trick.leadSuit;
    let best = trick.cards[0].card;
    for (let i = 1; i < trick.cards.length; i++) {
      const current = trick.cards[i].card;
      if (compareCards(current, best, leadSuit) > 0) {
        best = current;
      }
    }
    return best;
  }
};
function createMediumAI() {
  return new MediumAI();
}

// src/ai/CardTracker.ts
var CardTracker = class {
  /** All cards that have been played so far */
  playedCards = [];
  /** Cards known to be in specific players' hands (from observation) */
  knownVoids = /* @__PURE__ */ new Map();
  /**
   * Record a card that was played.
   */
  recordPlay(playerId, card, leadSuit) {
    this.playedCards.push(card);
    if (leadSuit !== null && card.suit !== leadSuit) {
      if (!this.knownVoids.has(playerId)) {
        this.knownVoids.set(playerId, /* @__PURE__ */ new Set());
      }
      this.knownVoids.get(playerId).add(leadSuit);
    }
  }
  /**
   * Update tracker from a completed round state.
   * Call this at the start to catch up on already-played tricks.
   */
  syncFromRound(round) {
    this.playedCards = [];
    this.knownVoids.clear();
    const leadSuit = round.currentTrick.leadSuit;
    for (const tc of round.currentTrick.cards) {
      this.recordPlay(tc.playerId, tc.card, leadSuit);
    }
  }
  /**
   * Get all cards that haven't been played yet (excluding our own hand).
   */
  getRemainingCards(myHand) {
    const allCards = createDeck();
    return allCards.filter(
      (c) => !this.playedCards.some((p) => cardEquals(p, c)) && !myHand.some((h) => cardEquals(h, c))
    );
  }
  /**
   * Get remaining cards of a specific suit.
   */
  getRemainingCardsOfSuit(suit, myHand) {
    return this.getRemainingCards(myHand).filter((c) => c.suit === suit);
  }
  /**
   * Get the number of remaining cards of a specific suit.
   */
  countRemainingInSuit(suit, myHand) {
    return this.getRemainingCardsOfSuit(suit, myHand).length;
  }
  /**
   * Get the highest remaining card of a suit.
   */
  getHighestRemaining(suit, myHand) {
    const remaining = this.getRemainingCardsOfSuit(suit, myHand);
    if (remaining.length === 0) return null;
    remaining.sort((a, b) => b.rank - a.rank);
    return remaining[0];
  }
  /**
   * Check if a specific player is known to be void in a suit.
   */
  isPlayerVoid(playerId, suit) {
    return this.knownVoids.get(playerId)?.has(suit) ?? false;
  }
  /**
   * Count how many trumps are still out (not in our hand, not played).
   */
  countRemainingTrumps(myHand) {
    return this.countRemainingInSuit(TRUMP_SUIT, myHand);
  }
  /**
   * Estimate probability that our card will win if played.
   * Simple heuristic: ratio of remaining cards it beats vs total remaining.
   */
  estimateWinProbability(card, myHand) {
    const remaining = this.getRemainingCards(myHand);
    if (remaining.length === 0) return 1;
    let beatsCount = 0;
    for (const other of remaining) {
      if (isTrump(card) && !isTrump(other)) {
        beatsCount++;
      } else if (isTrump(card) && isTrump(other)) {
        if (card.rank > other.rank) beatsCount++;
      } else if (!isTrump(card) && !isTrump(other)) {
        if (card.suit === other.suit && card.rank > other.rank) beatsCount++;
        if (card.suit !== other.suit) beatsCount++;
      }
    }
    return beatsCount / remaining.length;
  }
  /**
   * Get the number of cards that have been played.
   */
  getPlayedCount() {
    return this.playedCards.length;
  }
  /**
   * Reset the tracker for a new round.
   */
  reset() {
    this.playedCards = [];
    this.knownVoids.clear();
  }
};

// src/ai/HardAI.ts
var HardAI = class {
  tracker = new CardTracker();
  lastSyncedTricksPlayed = -1;
  makeCall(player, round) {
    const strength = evaluateHandStrength(player.hand);
    let estimate = strength.estimatedTricks;
    const callsMade = Object.keys(round.calls).length;
    const totalCalls = Object.values(round.calls).reduce((sum, c) => sum + c, 0);
    if (callsMade >= 2) {
      const avgCall = totalCalls / callsMade;
      if (avgCall > 4) {
        estimate = Math.max(2, estimate - 0.5);
      }
    }
    const trumpCount = player.hand.filter((c) => isTrump(c)).length;
    if (trumpCount >= 4) {
      estimate += 0.5;
    }
    const call = Math.round(estimate);
    return Math.max(2, Math.min(13, call));
  }
  selectCard(player, round) {
    const hand = player.hand;
    if (hand.length === 0) {
      throw new Error("No cards to play");
    }
    this.syncTracker(round);
    const leadSuit = round.currentTrick.leadSuit;
    if (leadSuit === null) {
      return this.selectLeadCard(hand, round, player);
    }
    if (hasCardOfSuit(hand, leadSuit)) {
      return this.selectFollowCard(hand, round, leadSuit, player);
    }
    return this.selectVoidCard(hand, round, player);
  }
  /**
   * Sync tracker with the current round state.
   * Only re-syncs when trick count changes.
   */
  syncTracker(round) {
    if (round.tricksPlayed !== this.lastSyncedTricksPlayed) {
      this.tracker.syncFromRound(round);
      this.lastSyncedTricksPlayed = round.tricksPlayed;
    }
  }
  /**
   * Leading strategy for HardAI:
   * 1. If we have a "sure winner" (highest remaining card of a suit), lead it
   * 2. Lead from longest non-trump suit to establish control
   * 3. Lead trumps to draw out opponents' trumps if we have many
   * 4. Fall back to highest non-trump
   */
  selectLeadCard(hand, _round, _player) {
    const nonTrumps = hand.filter((c) => !isTrump(c));
    const trumps = hand.filter((c) => isTrump(c));
    for (const card of nonTrumps) {
      const highest = this.tracker.getHighestRemaining(card.suit, hand);
      if (!highest || card.rank > highest.rank) {
        return card;
      }
    }
    if (nonTrumps.length > 0) {
      const suitCounts = /* @__PURE__ */ new Map();
      for (const card of nonTrumps) {
        if (!suitCounts.has(card.suit)) {
          suitCounts.set(card.suit, []);
        }
        suitCounts.get(card.suit).push(card);
      }
      let bestSuit = null;
      let bestCount = 0;
      for (const [suit, cards] of suitCounts) {
        const remaining = this.tracker.countRemainingInSuit(suit, hand);
        if (remaining > bestCount) {
          bestCount = remaining;
          bestSuit = suit;
        }
        if (remaining === bestCount && bestSuit !== null) {
          const currentMax = Math.max(...cards.map((c) => c.rank));
          const bestCards = suitCounts.get(bestSuit) ?? [];
          const bestMax = bestCards.length > 0 ? Math.max(...bestCards.map((c) => c.rank)) : 0;
          if (currentMax > bestMax) {
            bestSuit = suit;
          }
        }
      }
      if (bestSuit) {
        const suitCards = suitCounts.get(bestSuit);
        suitCards.sort((a, b) => b.rank - a.rank);
        return suitCards[0];
      }
    }
    if (trumps.length >= 3) {
      const remainingTrumps = this.tracker.countRemainingTrumps(hand);
      if (remainingTrumps > 0) {
        trumps.sort((a, b) => b.rank - a.rank);
        return trumps[0];
      }
    }
    const sorted = [...hand].sort((a, b) => b.rank - a.rank);
    return sorted[0];
  }
  /**
   * Following strategy for HardAI:
   * 1. If we can win and we need tricks, play lowest winning card
   * 2. If trick already high value and we can win cheaply, take it
   * 3. If we can't win or don't need tricks, play lowest
   */
  selectFollowCard(hand, round, leadSuit, player) {
    const suitCards = getCardsOfSuit(hand, leadSuit);
    suitCards.sort((a, b) => a.rank - b.rank);
    const currentWinner = this.getCurrentWinningCard(round);
    const tricksNeeded = this.getTricksNeeded(player, round);
    if (currentWinner) {
      const winners = suitCards.filter(
        (c) => compareCards(c, currentWinner, leadSuit) > 0
      );
      if (winners.length > 0) {
        if (tricksNeeded > 0) {
          return winners[0];
        }
        if (winners[0].rank === suitCards[0].rank) {
          return winners[0];
        }
        return suitCards[0];
      }
    }
    return suitCards[0];
  }
  /**
   * Void play strategy for HardAI:
   * 1. Decide whether to trump based on trick value and our needs
   * 2. If trumping, use the lowest trump that wins (or over-trumps)
   * 3. If discarding, throw from shortest non-trump suit to create more voids
   */
  selectVoidCard(hand, round, player) {
    const trumpCards = hand.filter((c) => isTrump(c));
    const nonTrumpCards = hand.filter((c) => !isTrump(c));
    const tricksNeeded = this.getTricksNeeded(player, round);
    if (trumpCards.length > 0 && tricksNeeded > 0) {
      const existingTrump = round.currentTrick.cards.find(
        (tc) => isTrump(tc.card)
      );
      if (existingTrump) {
        const overTrumps = trumpCards.filter((c) => c.rank > existingTrump.card.rank).sort((a, b) => a.rank - b.rank);
        if (overTrumps.length > 0) {
          return overTrumps[0];
        }
      } else {
        trumpCards.sort((a, b) => a.rank - b.rank);
        return trumpCards[0];
      }
    }
    if (nonTrumpCards.length > 0) {
      const suitCounts = /* @__PURE__ */ new Map();
      for (const card of nonTrumpCards) {
        if (!suitCounts.has(card.suit)) {
          suitCounts.set(card.suit, []);
        }
        suitCounts.get(card.suit).push(card);
      }
      let shortestSuit = null;
      let shortestCount = Infinity;
      for (const [suit, cards] of suitCounts) {
        if (cards.length < shortestCount) {
          shortestCount = cards.length;
          shortestSuit = suit;
        }
      }
      if (shortestSuit) {
        const suitCards = suitCounts.get(shortestSuit);
        suitCards.sort((a, b) => a.rank - b.rank);
        return suitCards[0];
      }
    }
    trumpCards.sort((a, b) => a.rank - b.rank);
    return trumpCards[0];
  }
  /**
   * Get the card currently winning the trick.
   */
  getCurrentWinningCard(round) {
    const trick = round.currentTrick;
    if (trick.cards.length === 0) return null;
    const leadSuit = trick.leadSuit;
    let best = trick.cards[0].card;
    for (let i = 1; i < trick.cards.length; i++) {
      const current = trick.cards[i].card;
      if (compareCards(current, best, leadSuit) > 0) {
        best = current;
      }
    }
    return best;
  }
  /**
   * Calculate how many more tricks we need to meet our call.
   */
  getTricksNeeded(player, _round) {
    if (player.call === null) return 3;
    return Math.max(0, player.call - player.tricksWon);
  }
  /**
   * Reset tracker for a new round.
   */
  reset() {
    this.tracker.reset();
    this.lastSyncedTricksPlayed = -1;
  }
};
function createHardAI() {
  return new HardAI();
}

// src/random.ts
function mulberry32(seed) {
  let state = seed | 0;
  return () => {
    state = state + 1831565813 | 0;
    let t = Math.imul(state ^ state >>> 15, 1 | state);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function createRNG(seed) {
  const generator = mulberry32(seed);
  return {
    get seed() {
      return seed;
    },
    next() {
      return generator();
    },
    nextInt(min, max) {
      return Math.floor(generator() * (max - min + 1)) + min;
    },
    shuffle(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(generator() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    }
  };
}
function seededShuffle(array, seed) {
  const rng = createRNG(seed);
  const copy = [...array];
  return rng.shuffle(copy);
}
export {
  AIDifficulty,
  ActionType,
  BONUS_CALL_THRESHOLD,
  CARDS_PER_PLAYER,
  CardTracker,
  EasyAI,
  GameEventType,
  GamePhase,
  HardAI,
  MAX_CALL,
  MAX_PLAYERS,
  MIN_CALL,
  MIN_PLAYERS,
  MediumAI,
  PlayerType,
  Rank,
  Suit,
  TRUMP_SUIT,
  WINNING_SCORE,
  addPlayer,
  calculateRoundResults,
  calculateRoundScore,
  cardEquals,
  cardToString,
  compareCards,
  countGuaranteedTricks,
  createCard,
  createDeck,
  createEasyAI,
  createGame,
  createHardAI,
  createMediumAI,
  createPlayer,
  createRNG,
  createRound,
  createTrick,
  deal,
  dealToPlayer,
  determineTrickWinner,
  evaluateHandStrength,
  gameReducer,
  getCardsOfSuit,
  getLeadSuit,
  getRankName,
  getWinner,
  groupBySuit,
  hasCardOfSuit,
  incrementTricksWon,
  isGameOver,
  isHigherRank,
  isTrump,
  isValidCall,
  isValidMove,
  makeCall,
  playCard,
  playCardToTrick,
  processCall,
  processPlayCard,
  removeCardFromHand,
  removePlayer,
  seededShuffle,
  setCall,
  shuffle,
  sortHand,
  startGame,
  suggestCall,
  updateScore
};
//# sourceMappingURL=index.js.map